import React, { useState, useEffect } from 'react';
import { a2aTeacherService } from '../../a2a/services/a2aTeacherService.js';
import { a2aClassService } from '../../a2a/services/a2aClassService.js';
import { getTenantIdFromUser } from '../../a2a/tenantHelper.js';
import './StudentManagementModule.css';

const StudentManagementModule = ({ currentUser, onBack, onTabChange }) => {
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (currentUser) {
      fetchClasses();
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      fetchStudents();
    }
  }, [selectedClass, currentUser]);

  const fetchClasses = async () => {
    try {
      // Get tenant_id using helper (multi-tenant support)
      const tenantId = getTenantIdFromUser(currentUser);
      const result = await a2aClassService.getClasses({
        tenantId: tenantId,
        page: 1,
        limit: 100
      });
      
      if (result.success) {
        // Filter classes for this teacher
        let teacherClasses = result.data || [];
        if (currentUser?.id) {
          teacherClasses = teacherClasses.filter(cls => 
            cls.teacher_id === currentUser.id || 
            cls.class_teacher_id === currentUser.id ||
            cls.assigned_teachers?.includes(currentUser.id)
          );
        }
        setClasses(teacherClasses);
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const fetchStudents = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get tenant_id using helper (multi-tenant support)
      const tenantId = getTenantIdFromUser(currentUser);
      const teacherId = currentUser?.id || currentUser?.user?.id;
      
      if (!teacherId) {
        console.warn('Cannot fetch students: teacher ID is missing');
        setStudents([]);
        return;
      }
      
      // Fetch students for the teacher
      const result = await a2aTeacherService.getTeacherStudents({
        teacher_id: teacherId,
        class_id: selectedClass?.id,
        tenant_id: tenantId
      });
      
      if (result.success) {
        setStudents(result.data || []);
      } else {
        console.error('Failed to fetch students:', result.error);
        setStudents([]);
      }
      
    } catch (error) {
      console.error('Error fetching students:', error);
      setError(error.message || 'Failed to fetch students');
    } finally {
      setLoading(false);
    }
  };

  const handleClassChange = (e) => {
    const classId = e.target.value;
    const selected = classes.find(cls => (cls.id || cls.class_id) === classId);
    setSelectedClass(selected || null);
  };

  const handleStudentClick = (student) => {
    onTabChange('student-info', { studentId: student.id || student.student_id });
  };

  const filteredStudents = students.filter(student => {
    const matchesSearch = !searchQuery || 
      (student.name && student.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (student.student_id && student.student_id.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesSearch;
  });

  return (
    <div className="student-management-module">
      <div className="module-header">
        <button className="back-button" onClick={onBack}>← 返回</button>
        <h2>學生管理</h2>
        <button className="refresh-button" onClick={fetchStudents} title="重新載入">
          🔄
        </button>
      </div>
      
      <div className="filters-section">
        <div className="filter-group">
          <label htmlFor="class-filter">班級:</label>
          <select 
            id="class-filter"
            value={selectedClass?.id || selectedClass?.class_id || ''} 
            onChange={handleClassChange}
            className="filter-select"
          >
            <option value="">所有班級</option>
            {classes.map(cls => (
              <option 
                key={cls.id || cls.class_id} 
                value={cls.id || cls.class_id}
              >
                {cls.name || cls.class_name}
              </option>
            ))}
          </select>
        </div>
        
        <div className="filter-group">
          <label htmlFor="search-input">搜尋:</label>
          <input
            id="search-input"
            type="text"
            placeholder="搜尋學生姓名或學號..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="filter-input"
          />
        </div>
      </div>
      
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>載入中...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <p>錯誤: {error}</p>
          <button onClick={fetchStudents} className="retry-button">
            重試
          </button>
        </div>
      ) : (
        <div className="students-list">
          {filteredStudents.length > 0 ? (
            filteredStudents.map((student, index) => (
              <div 
                key={student.id || student.student_id || index}
                className="student-card"
                onClick={() => handleStudentClick(student)}
              >
                <div className="student-avatar">
                  {student.name ? student.name.charAt(0) : 'S'}
                </div>
                <div className="student-info">
                  <h4>{student.name || `學生 ${index + 1}`}</h4>
                  {student.student_id && (
                    <p className="student-id">學號: {student.student_id}</p>
                  )}
                  {student.class_name && (
                    <p className="student-class">班級: {student.class_name}</p>
                  )}
                  {student.grade && (
                    <p className="student-grade">年級: {student.grade}</p>
                  )}
                </div>
                <div className="student-actions">
                  <button 
                    className="view-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStudentClick(student);
                    }}
                  >
                    查看詳情
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="no-students">
              <p>暫無學生資料</p>
              {selectedClass && (
                <p className="hint">所選班級暫無學生</p>
              )}
              {!selectedClass && (
                <p className="hint">請選擇班級以查看學生</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StudentManagementModule;

